import { useEffect, useState } from 'react'
import { subscribeToAiSuggestions } from '@/api/ws'
import { auditsApi } from '@/api/client'
import type { AiSuggestionMessage, SuggestionDraft } from '@/api/client'

interface AiSuggestionsState {
  /** Completed suggestions (`ready` frames + REST hydrate). */
  suggestions: AiSuggestionMessage[]
  /** In-flight token previews keyed by auditCheckId. */
  drafts: SuggestionDraft[]
  errors: string[]
  /** True until the BFF emits a `done` frame on the WS topic. */
  streaming: boolean
}

/**
 * Streams AI suggestions for an in-flight audit:
 *
 * - `started` → opens a {@link SuggestionDraft} the UI can render as a placeholder
 * - `delta`   → appends the chunk to the matching draft's text
 * - `ready`   → moves the draft into the completed suggestions array
 * - `error`   → appended to {@code errors}
 * - `done`    → flips {@code streaming} to false; UI hides spinners
 *
 * On mount also fetches any already-persisted suggestions via REST so a late-arriving
 * subscriber sees the full set.
 */
export function useAiSuggestions(
  auditId: string | undefined,
  opts?: {
    /**
     * Start with streaming=false — for pages showing an already-completed
     * report, where no generation is running until the user starts one
     * (client-gathered excerpt flow). A `started` frame flips it back on.
     */
    initialStreaming?: boolean
  },
) {
  const [state, setState] = useState<AiSuggestionsState>({
    suggestions: [],
    drafts: [],
    errors: [],
    streaming: opts?.initialStreaming ?? true,
  })

  useEffect(() => {
    if (!auditId) return
    let cancelled = false

    // REST hydrate — covers reload of an old report. If any suggestions are
    // already persisted, the BFF's stream is by definition finished, so also
    // flip streaming=false to avoid a permanent spinner alongside the cards.
    auditsApi.getSuggestions(auditId).then(initial => {
      if (cancelled || initial.length === 0) return
      console.debug('[useAiSuggestions] hydrated %d persisted suggestion(s) for %s',
        initial.length, auditId)
      setState(prev => ({
        ...prev,
        suggestions: mergeUniqueSuggestions(prev.suggestions, initial),
        streaming: false,
      }))
    }).catch(() => { /* not ready yet; STOMP will catch up */ })

    const unsub = subscribeToAiSuggestions(auditId, msg => {
      if (cancelled) return
      console.debug('[useAiSuggestions] %s frame for %s', msg.type, auditId, msg)
      setState(prev => applyMessage(prev, msg))
    })

    return () => {
      cancelled = true
      unsub()
    }
  }, [auditId])

  return state
}

function applyMessage(prev: AiSuggestionsState, msg: AiSuggestionMessage): AiSuggestionsState {
  switch (msg.type) {
    case 'done':
      return { ...prev, drafts: [], streaming: false }

    case 'error': {
      // Per-card errors carry auditCheckId — close that card's draft so it
      // doesn't sit in streaming state until the batch-level `done`.
      const drafts = msg.auditCheckId
        ? prev.drafts.filter(d => d.auditCheckId !== msg.auditCheckId)
        : prev.drafts
      return {
        ...prev,
        drafts,
        errors: msg.error ? [...prev.errors, msg.error] : prev.errors,
      }
    }

    case 'started': {
      if (!msg.auditCheckId) return prev
      // Replace any existing draft for this audit (handles retry / re-emission)
      const existing = prev.drafts.filter(d => d.auditCheckId !== msg.auditCheckId)
      return {
        ...prev,
        // A new generation batch may begin after mount (client-gathered flow)
        streaming: true,
        drafts: [...existing, {
          auditCheckId: msg.auditCheckId,
          auditTitle: msg.auditTitle,
          text: '',
        }],
      }
    }

    case 'delta': {
      if (!msg.auditCheckId || !msg.chunk) return prev
      // Append to the matching draft, creating one if it didn't exist yet
      // (covers the case where a delta arrives before `started`, which can
      // happen with STOMP topic ordering hiccups).
      const found = prev.drafts.find(d => d.auditCheckId === msg.auditCheckId)
      if (found) {
        return {
          ...prev,
          drafts: prev.drafts.map(d =>
            d.auditCheckId === msg.auditCheckId
              ? { ...d, text: d.text + msg.chunk }
              : d,
          ),
        }
      }
      return {
        ...prev,
        drafts: [...prev.drafts, {
          auditCheckId: msg.auditCheckId,
          auditTitle: msg.auditTitle,
          text: msg.chunk,
        }],
      }
    }

    case 'ready': {
      // Promote draft → completed suggestion
      const drafts = msg.auditCheckId
        ? prev.drafts.filter(d => d.auditCheckId !== msg.auditCheckId)
        : prev.drafts
      return {
        ...prev,
        drafts,
        suggestions: mergeUniqueSuggestions(prev.suggestions, [msg]),
      }
    }

    default:
      return prev
  }
}

function mergeUniqueSuggestions(
  existing: AiSuggestionMessage[],
  incoming: AiSuggestionMessage[],
): AiSuggestionMessage[] {
  const seen = new Set(existing.map(s => s.id ?? `${s.auditCheckId}-${s.createdAt}`))
  const merged = [...existing]
  for (const next of incoming) {
    const key = next.id ?? `${next.auditCheckId}-${next.createdAt}`
    if (!seen.has(key)) {
      seen.add(key)
      merged.push(next)
    }
  }
  return merged
}
